import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import { CallLogIcon, VoicemailIcon } from '../assets/icons';
import { formatPhoneNumberWithCountryCode } from '../utils/phoneFormatter';

// Get API URL from environment
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : process.env.REACT_APP_API_URL || 'http://localhost:8040';

const CallLogs = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    totalCalls: 0,
    todayCalls: 0
  });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const itemsPerPage = 20;

  // Handle URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlDateFrom = searchParams.get('date_from');
    const urlDateTo = searchParams.get('date_to');
    const urlSearch = searchParams.get('search');

    if (urlDateFrom) {
      setDateFrom(urlDateFrom);
    }
    if (urlDateTo) {
      setDateTo(urlDateTo);
    }
    if (urlSearch) {
      setSearchTerm(urlSearch);
    }
  }, [location.search]);

  useEffect(() => {
    fetchCallLogs();
    fetchStats();
  }, [currentPage, searchTerm, dateFrom, dateTo]);

  const fetchCallLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: itemsPerPage.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }

      const response = await fetch(`${API_URL}/api/call-logs/?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCallLogs(data.items || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching call logs:', err);
      setError(t('callLogs.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }

      const response = await fetch(`${API_URL}/api/call-logs/stats?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          totalCalls: data.total_calls || 0,
          todayCalls: data.today_calls || 0
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);

    // Update URL with current filters including search term
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (term) params.append('search', term);

    const newUrl = params.toString() ? `?${params.toString()}` : '';
    navigate(`/call-logs${newUrl}`, { replace: true });
  };

  const handleDateFilter = () => {
    setCurrentPage(1);
    // Update URL with current filters
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (searchTerm) params.append('search', searchTerm);

    const newUrl = params.toString() ? `?${params.toString()}` : '';
    navigate(`/call-logs${newUrl}`, { replace: true });

    fetchCallLogs();
    fetchStats();
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setCurrentPage(1);
    // Clear URL parameters
    navigate('/call-logs', { replace: true });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const countryCodes = [
    '+852',
    '+853',
    '+86',
    '+886',
    '+81',
    '+82',
    '+65',
    '+60',
    '+66',
    '+673',
    '+84',
    '+855',
    '+856',
    '+886',
  ]

  const formatPhoneNumber = (phoneNumber) => {
    return formatPhoneNumberWithCountryCode(phoneNumber);
  };

  const getWhatsAppLink = (phoneNumber) => {
    if (!phoneNumber) return '#';
    // Remove any spaces, dashes, or other formatting
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    return `https://wa.me/${cleanNumber}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  if (loading && callLogs.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#075e54]"></div>
        <p className="ml-4 text-gray-600">{t('callLogs.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <CallLogIcon className="h-8 w-8 text-[#075e54] mr-3" />
          <h1 className="text-2xl font-bold text-gray-800">{t('callLogs.title')}</h1>
        </div>
        <p className="text-gray-600">{t('callLogs.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <CallLogIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">{t('callLogs.totalCalls')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCalls}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <CallLogIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">{t('callLogs.todayCalls')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todayCalls}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="md:col-span-2">
            <SearchBar
              onSearch={handleSearch}
              placeholder={t('callLogs.search')}
              value={searchTerm}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('callLogs.dateFrom')}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('callLogs.dateTo')}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleDateFilter}
            className="px-4 py-2 bg-[#075e54] text-white rounded-md hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-[#075e54]"
          >
            {t('callLogs.filter')}
          </button>
          <button
            onClick={handleClearFilter}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {t('callLogs.clear')}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Call Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#075e54]"></div>
            <p className="ml-2 text-gray-600">{t('callLogs.loading')}</p>
          </div>
        ) : callLogs.length === 0 ? (
          <div className="text-center py-12">
            <CallLogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('callLogs.noResults')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('callLogs.timestamp')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('callLogs.fromNumber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('callLogs.customer')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('callLogs.message')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('callLogs.type')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {callLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <a
                          href={getWhatsAppLink(log.to_number)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {formatPhoneNumber(log.to_number)}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.customer_name || log.conversation_name || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="flex items-center space-x-2">
                          {log.is_voicemail && log.id && (
                            <a
                              href={`https://m.nexicare.ai/tr/${log.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                              title={t('callLogs.playVoicemail', 'Play Voicemail')}
                            >
                              <VoicemailIcon className="h-4 w-4" />
                              <span className="text-xs">{t('callLogs.listen', 'Listen')}</span>
                            </a>
                          )}
                          <span className={log.is_voicemail && log.id ? '' : 'truncate'}>
                            {log.message_content || '-'}
                          </span>
                        </div>
                        {log.is_voicemail && log.audio_duration && (
                          <div className="text-xs text-gray-500 mt-1">
                            {t('callLogs.duration')}: {formatDuration(log.audio_duration)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {log.is_voicemail ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <VoicemailIcon className="h-3 w-3 mr-1" />
                            {t('callLogs.voicemail')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t('callLogs.call')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CallLogs;
